import React, { useState, useRef, useEffect, useCallback, RefObject, MouseEvent, JSX } from "react";
import { FRAGMENT_MIN_HEIGHT } from "components/utils";
import { addNewChange } from "utils";
import { TrackSC } from "./styled";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { CueTime } from "../../../../elements/CueTime";
import { useFolderOffsetMs } from "hooks/useFolderOffsetMs";
import { toGlobalMs } from "utils/globalTime";
import { AudioTrack } from "providers/AudioProvider/types";
import { HorizontalLineSC, SelectWrapperSC } from "../DraggingTrackTranslated/styled";
import { SelectedTranslatedTrack } from "providers/AudioProvider/types";
import { SelectCheckbox } from "../DraggingTrackTranslated/components/SelectCheckbox";
import { voicingWasMoved } from "../DraggingTrackTranslated/utils";
import { useUiAudioStateContext } from "../../../../../hooks/useUiAudioStateContext";

interface DraggingSubtitleProps {
    id: string;
    ind: number;
    oneTrack: AudioTrack;
    start: number;
    length: number;
    trimmed: number | null;
    trimmed_start: number;
    tracks: AudioTrack[];
    timeoutRef: RefObject<ReturnType<typeof setTimeout> | null>;
    draggingTrack: RefObject<HTMLDivElement | null>;
    children: React.ReactNode;
    selectedOriginalTrackIds: SelectedTranslatedTrack[];
    setSelectedOriginalTrackIds: React.Dispatch<React.SetStateAction<SelectedTranslatedTrack[]>>;
}

export const DraggingTrackOriginal = React.memo(
    ({
        id,
        ind,
        oneTrack,
        start,
        length,
        trimmed,
        trimmed_start,
        tracks,
        timeoutRef,
        draggingTrack,
        children,
        selectedOriginalTrackIds,
        setSelectedOriginalTrackIds,
    }: DraggingSubtitleProps) => {
        const handles = useAudioHandlesContext();
        const state = useAudioStateContext();
        const uiState = useUiAudioStateContext();
        const zoomLevel = 100 - state["zoomLevel"];
        const folderOffsetMs = useFolderOffsetMs();
        // Расстояние до следующей реплики: по нему видно, поместится ли вторая строка времени,
        // не наехав на метку соседа. Последняя реплика ограничений не имеет.
        const nextTrackStart = tracks[ind + 1]?.start;
        const gapToNextPx =
            nextTrackStart === undefined
                ? undefined
                : (nextTrackStart - oneTrack.start) / zoomLevel;
        const [position, setPosition] = useState(start);
        const [lengthPosition, setLengthPosition] = useState(length);
        const [isDragging, setIsDragging] = useState(false);
        const [isLengthing, setIsLengthing] = useState(false);
        const [isLengthingUpper, setIsLengthingUpper] = useState(false);
        const [startY, setStartX] = useState(0);
        const [lengthX, setLengthX] = useState(0);
        const hideEditers = false;

        useEffect(() => {
            if (length !== lengthPosition) {
                setLengthPosition(length);
            }
        }, [length]);

        useEffect(() => {
            if (start !== position) {
                setPosition(start);
            }
        }, [start]);

        const getTrackClass = (trackId: string, tracks: AudioTrack[]) => {
            const { playingFragment, playingFragmentNext, changed } = state;
            // const playingFragment = id;

            let className = "";
            if (changed.includes(trackId)) className += " changed";
            if (trackId === playingFragment) className += " active";
            if (trimmed) className += " trimmed";
            if (state.changedRecently.includes(id)) className += " changed_recently";

            // Determine if this track is the next one to play and is currently in the delay period
            const isNextTrackInDelay =
                trackId === playingFragmentNext?.id && timeoutRef && timeoutRef.current;

            if (isNextTrackInDelay || trackId === handles.getNextTrackId(playingFragment, tracks)) {
                className += " next";
            }

            return className;
        };

        const dragHandleRef = useRef<HTMLDivElement | null>(null);
        const dragHandleRefStart = useRef<HTMLDivElement | null>(null);
        const lengthHandleRef = useRef<HTMLDivElement | null>(null);

        const clickHandler = () => {
            console.log("CLICKED = ", oneTrack);
            handles.setPlayingFragment(null);
            handles.setPlayStart(position);
            handles.setPlayOriginalStart(oneTrack.start);
            handles.setPlaying(true);
            handles.setVoiceoverOnlyPlayback(false);
            handles.setPlaySavedTrack(false);
            handles["setZoomFragment"]([
                startY / zoomLevel,
                (startY + state.scrubberWidth) / zoomLevel,
            ]);
            handles.setPlaybackTime(oneTrack["start"]);
            handles["setPlayingFragmentNext"](null);
            handles.setStartPlaying(true);
            handles.setLastActiveSubtitle({ playOriginalStart: oneTrack.start, playbackTime: oneTrack.start });
            // Also set voiceover so Right shortcut plays from the same row
            const translatedTracks = state.translationTrackAll[state.lang];
            const corresponding = translatedTracks?.find((t: AudioTrack) => t.original_id === oneTrack.original_id);
            if (corresponding?.file) {
                handles.setLastActiveVoiceover({ trackId: corresponding.id, playOriginalStart: corresponding.original_start, playbackTime: corresponding.start });
            }
        };

        const handlePositionMouseDown = (e: MouseEvent<HTMLDivElement>) => {
            if ((e.target as HTMLDivElement).dataset.track === "TrackSC") {
                e.preventDefault();
                e.stopPropagation();
            }

            if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
                setIsDragging(true);
                setStartX(e.clientY);
            }

            if (
                dragHandleRefStart.current &&
                dragHandleRefStart.current.contains(e.target as Node)
            ) {
                setIsLengthingUpper(true);
                setStartX(e.clientY);
            }
            if (
                dragHandleRef.current &&
                !dragHandleRef.current.contains(e.target as Node) &&
                !lengthHandleRef.current?.contains(e.target as Node) &&
                !dragHandleRefStart.current?.contains(e.target as Node) &&
                (e.target as HTMLDivElement).dataset.track === "TrackSC"
            ) {
                clickHandler();
            }
        };
        const handleLengthMouseDown = (e: MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (lengthHandleRef.current && lengthHandleRef.current.contains(e.target as Node)) {
                setIsLengthing(true);
                setLengthX(e.clientY);
            }
        };

        const getMaxLength = () => {
            const isLastTrack = ind === tracks.length - 1;

            const min = ind === 0 ? 0 : tracks[ind - 1].start + tracks[ind - 1].length;
            const max = isLastTrack ? state.durationFull : tracks[ind + 1].start;

            return { min, max };
        };

        const getMoveBounds = () => {
            if (state.disableMovementRestrictions) {
                return { min: 0, max: state.durationFull };
            }
            return getMaxLength();
        };

        const handleMouseMove = useCallback(
            (e: PointerEvent) => {
                if (state.subtitleEditing) {
                    return;
                }
                e.preventDefault();
                if (isDragging || isLengthingUpper) {
                    const { min, max } = getMoveBounds();
                    const deltaX = e.clientY - startY;
                    const newPosition = position + deltaX * zoomLevel;
                    const newLengthPosition = lengthPosition - deltaX * zoomLevel;
                    if (
                        newPosition >= min &&
                        (isDragging ? lengthPosition : 0) + newPosition <= max &&
                        newLengthPosition > 0
                    ) {
                        setPosition(newPosition);
                        if (isLengthingUpper) {
                            if (
                                newPosition + newLengthPosition >= min &&
                                newPosition + newLengthPosition <= max
                            ) {
                                setLengthPosition(newLengthPosition);
                            }
                        }

                        setStartX(e.clientY);
                    }
                }
                if (isLengthing) {
                    const { min, max } = getMoveBounds();
                    const deltaX = e.clientY - lengthX;
                    const newLengthPosition = lengthPosition + deltaX * zoomLevel;
                    if (newLengthPosition > 1 && position + newLengthPosition <= max) {
                        setLengthPosition(newLengthPosition);
                        setLengthX(e.clientY);
                    }
                }
            },
            [
                isDragging,
                isLengthing,
                position,
                lengthPosition,
                startY,
                lengthX,
                state.subtitleEditing,
                state.durationFull,
                state.disableMovementRestrictions,
            ],
        );

        // Сохраняется только то, что сдвинули, — как у озвучки (voicingWasMoved, PR #292). Здесь
        // стояло сравнение координаты указателя (пиксели) с началом реплики (мс): оно не совпадало
        // никогда, и нажатие на ручку без движения сохраняло «начало + длина» как правку
        // переводчика. На fdcde2a1 так легла в файл новая реплика с концом −153841.
        // Отрицательную или нулевую длину не отправляем никогда — сервер её не проверяет.
        const cueWasMoved = () =>
            lengthPosition > 0 && voicingWasMoved({ position, start, lengthPosition, length });

        useEffect(() => {
            // Конец перетаскивания — и мышью (isDragging), и за верхнюю ручку (isLengthingUpper):
            // раньше конец растяжки сверху не сохранялся вовсе.
            if (!isDragging && !isLengthingUpper && startY && cueWasMoved()) {
                (async () => {
                    try {
                        const accepted = await handles.updatePosition({
                            id: oneTrack.original_id,
                            newPosition: position,
                            newLength: lengthPosition,
                            tracks,
                            editedLang: state.originalLang,
                        });
                        // Провайдер отказал (испорченное время, причина — в полосе ошибок): реплика
                        // возвращается туда, где она лежит, а не остаётся нарисованной сдвинутой.
                        if (accepted === false) {
                            setPosition(start);
                            setLengthPosition(length);
                        }
                    } catch (error) {
                        console.error("Error updating position or length:", error);
                    }
                })();
            }
        }, [isDragging, isLengthingUpper]);

        useEffect(() => {
            if (!isLengthing && lengthX && cueWasMoved()) {
                (async () => {
                    try {
                        const accepted = await handles.updateLength({
                            id: oneTrack.original_id,
                            newLength: lengthPosition,
                            start: oneTrack.start,
                            editedLang: state.originalLang,
                        });
                        if (accepted === false) {
                            setLengthPosition(length);
                        }
                    } catch (error) {
                        console.error("Error updating length:", error);
                    }
                })();
            }
        }, [isLengthing]);

        const handleMouseUp = (e: PointerEvent) => {
            e.preventDefault();
            setIsDragging(false);
            setIsLengthing(false);
            setIsLengthingUpper(false);
        };

        useEffect(() => {
            if (draggingTrack.current) {
                draggingTrack.current.addEventListener("pointermove", handleMouseMove);
                draggingTrack.current.addEventListener("pointerup", handleMouseUp);
            }
            return () => {
                if (draggingTrack.current) {
                    draggingTrack.current.removeEventListener("pointermove", handleMouseMove);
                    draggingTrack.current.removeEventListener("pointerup", handleMouseUp);
                }
            };
        }, [isDragging, isLengthing, handleMouseMove, handleMouseUp]);

        const handleTouchStart = (e: TouchEvent) => {
            if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
                setStartX(e.touches[0].clientY);
            } else if (
                dragHandleRefStart.current &&
                dragHandleRefStart.current.contains(e.target as Node)
            ) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
                setIsLengthingUpper(true);
                setStartX(e.touches[0].clientY);
            }
            // If the touch is outside the drag handles, do nothing, allowing parent scroll
        };

        const handleTouchMove = useCallback(
            (e: TouchEvent) => {
                if (state.subtitleEditing) {
                    return;
                }
                if (isDragging || isLengthingUpper) {
                    e.preventDefault();
                    e.stopPropagation();
                    const touch = e.touches[0];
                    const { min, max } = getMoveBounds();
                    const deltaX = touch.clientY - startY;
                    const newPosition = position + deltaX * zoomLevel;
                    const newLengthPosition = lengthPosition - deltaX * zoomLevel;

                    if (
                        newPosition >= min &&
                        (isDragging ? lengthPosition : 0) + newPosition <= max &&
                        newLengthPosition > 0
                    ) {
                        setPosition(newPosition);

                        if (isLengthingUpper) {
                            if (
                                newPosition + newLengthPosition >= min &&
                                newPosition + newLengthPosition <= max
                            ) {
                                setLengthPosition(newLengthPosition);
                            }
                        }

                        setStartX(touch.clientY);
                    }
                }
                // If not dragging, do nothing, allowing parent scroll
            },
            [
                isDragging,
                position,
                lengthPosition,
                startY,
                isLengthingUpper,
                state.subtitleEditing,
                state.durationFull,
                state.disableMovementRestrictions,
            ],
        );

        const handleTouchEnd = (e: TouchEvent) => {
            if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                setIsLengthingUpper(false);
            }
        };

        useEffect(() => {
            if (draggingTrack.current) {
                draggingTrack.current.addEventListener("touchstart", handleTouchStart, {
                    passive: false,
                });
                document.addEventListener("touchmove", handleTouchMove, { passive: false });
                document.addEventListener("touchend", handleTouchEnd);
            }
            return () => {
                if (draggingTrack.current) {
                    draggingTrack.current.removeEventListener("touchstart", handleTouchStart);
                }
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            };
        }, [handleTouchMove]);

        return (
            <TrackSC
                top={`${position / zoomLevel}px`}
                height={`${Math.max(lengthPosition / zoomLevel, FRAGMENT_MIN_HEIGHT)}px`}
                shadow={
                    trimmed_start
                        ? `inset ${trimmed_start / zoomLevel}px 0 0 0px rgba(255, 0, 0, 0.7)`
                        : ""
                }
                layer={`${trimmed ? 1 : "inherit"}`}
                className={getTrackClass(oneTrack.id, tracks)}
                data-track={"TrackSC"}
                data-start-ms={Math.round(position)}
                data-length-ms={Math.round(lengthPosition)}
                onMouseDown={handlePositionMouseDown}
                isactive={
                    uiState.activeCaptionTrack
                        ? (uiState.activeCaptionTrack === oneTrack.id).toString()
                        : "none"
                }
            >
                <CueTime
                    localMs={oneTrack["start_rus_milli"]}
                    globalMs={toGlobalMs(oneTrack["start_rus_milli"], folderOffsetMs)}
                    showGlobal={state.showGlobalTime}
                    side="left"
                    gapToNextPx={gapToNextPx}
                />
                <HorizontalLineSC />

                <div>
                    {!hideEditers && !isDragging && (
                        <div
                            className={
                                isLengthing
                                    ? "x_track_length0 x_track_length0--v dragging"
                                    : "x_track_length0 x_track_length0--v"
                            }
                        >
                            <div
                                className="x_track_length x_track_length--v"
                                ref={lengthHandleRef}
                                style={
                                    state.subtitleEditing
                                        ? { background: "#EBEBE4", cursor: "no-drop" }
                                        : undefined
                                }
                                onMouseDown={handleLengthMouseDown}
                            >
                                {!isLengthing && <div className="ico ico--v">drag_indicator</div>}
                                {isLengthing && (
                                    <svg
                                        className="x_track_drag-icon"
                                        width={20}
                                        height={14}
                                        viewBox="0 0 20 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7 1.2071V12.7929C7 13.2383 6.46143 13.4614 6.14645 13.1464L0.707108 7.7071C0.316584 7.31658 0.316585 6.68341 0.707108 6.29289L6.14645 0.853549C6.46143 0.538567 7 0.761651 7 1.2071ZM13 1.2071V12.7929C13 13.2383 13.5386 13.4614 13.8536 13.1464L19.2929 7.7071C19.6834 7.31658 19.6834 6.68341 19.2929 6.29289L13.8536 0.853549C13.5386 0.538567 13 0.761651 13 1.2071Z"
                                            fill="white"
                                        />
                                    </svg>
                                )}
                            </div>
                        </div>
                    )}

                    {state.waitingCut.includes(oneTrack.original_id) && (
                        <div className="p_b_preloader">
                            <img
                                src="https://assets-global.website-files.com/651f0451acd1cf1eb674532d/657223dc690f13a86f06e953_preloader.gif"
                                loading="lazy"
                                className="preloadergif"
                                alt={""}
                            />
                        </div>
                    )}

                    {!isLengthing && (
                        <div
                            className="x_track_drag0 x_track_drag0--v"
                            ref={dragHandleRef}
                            style={{ right: 0, display: hideEditers ? "none" : "flex" }}
                        >
                            <div
                                className="x_track_drag-handle x_track_drag-handle--v"
                                style={
                                    state.subtitleEditing
                                        ? { background: "#EBEBE4", cursor: "no-drop" }
                                        : undefined
                                }
                            >
                                <svg
                                    className="x_track_drag-icon"
                                    width={20}
                                    height={14}
                                    viewBox="0 0 20 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7 1.2071V12.7929C7 13.2383 6.46143 13.4614 6.14645 13.1464L0.707108 7.7071C0.316584 7.31658 0.316585 6.68341 0.707108 6.29289L6.14645 0.853549C6.46143 0.538567 7 0.761651 7 1.2071ZM13 1.2071V12.7929C13 13.2383 13.5386 13.4614 13.8536 13.1464L19.2929 7.7071C19.6834 7.31658 19.6834 6.68341 19.2929 6.29289L13.8536 0.853549C13.5386 0.538567 13 0.761651 13 1.2071Z"
                                        fill="white"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
                <SelectWrapperSC
                    side={"left"}
                    onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                >
                    <SelectCheckbox
                        selectedTranslatedTrackIds={selectedOriginalTrackIds}
                        setSelectedTranslatedTrackIds={setSelectedOriginalTrackIds}
                        track={oneTrack}
                        ind={ind}
                    />
                </SelectWrapperSC>
                <HorizontalLineSC bottom={"true"} />
                {children}
            </TrackSC>
        );
    },
);
